import { useMutation } from "@tanstack/react-query";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { guacHttpClient } from "@/lib/http";
import { userSheetViewStore } from "@/stores/userSheetView";
import { HttpResponse, RecomputeCommuteResponse } from "@/types";

// Recomputes the candidacy's commute on the server — synchronously, so the
// response carries the fresh values — and writes them into the candidacy-info
// cache.
export const useUpdateSetCandidacyCommute = () => {
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const setCandidacyInfo = useSetCandidacyInfo();
  const setIsCommuteRefreshing = userSheetViewStore(
    (s) => s.setIsCommuteRefreshing,
  );

  return useMutation({
    mutationFn: async ({
      candidacyId,
      location,
    }: {
      candidacyId: string;
      location: string;
    }) => {
      const path = `companies/${currentCompanyId}/jobs/${currentJobId}/candidacies/${candidacyId}/recompute-commute`;
      const res = await guacHttpClient.post<
        RecomputeCommuteResponse,
        HttpResponse<RecomputeCommuteResponse>
      >(path, { location });
      return res.data;
    },
    onSuccess: ({ commuteDistanceMiles, commuteTimeMins }, { candidacyId }) => {
      setCandidacyInfo({ candidacyId, commuteDistanceMiles, commuteTimeMins });
    },
    // Hook-level so the refreshing flag always clears, even when the caller
    // unmounts before the recompute resolves (mutate()-level callbacks are
    // skipped then, which left the header metric spinning forever).
    onSettled: () => {
      setIsCommuteRefreshing(false);
    },
  });
};
